import {
  Button,
  makeStyles,
  mergeClasses,
  Text,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import { Copy24Regular, Document24Regular } from "@fluentui/react-icons";
import React, { useState } from "react";
import { useNotification } from "../../../../contextApis/NotificationContext";
import { copyToClipboardAddin } from "../../../../utils/copy";
import { formatToCurrency } from "../../../../utils/currency";
import { formatMiles } from "../../../../utils/miles";
import OfferPrice from "../../../Drayage/Search/OfferPrice/OfferPrice";
import RouteConnector from "../../../Drayage/Search/RouteCard/RouteConnector/RouteConnector";
import RenderIf from "../../../common/RenderIf/RenderIf";
import { RouteCostBrakeDown } from "../../../../types/otr";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    height: "max-content",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow2,
    padding: "16px",
  },
  content: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    borderRadius: "7px",
  },
  routeInfo: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%",
    paddingBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  locationWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  closeButton: {
    lineHeight: tokens.lineHeightBase100,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
  },
  costInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    cursor: "pointer",
    paddingBottom: "8px",
    borderRadius: "7px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  costInfoRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  grayedOut: {
    color: tokens.colorNeutralForeground3,
  },
  detailedInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingBottom: "8px",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  detailRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    justifyContent: "flex-end",
  },
  textWidth: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: "0 0 92px",
    maxWidth: "92px",
    minWidth: "92px",
  },
  alignEnd: {
    width: "92px",
    minWidth: "92px",
    textAlign: "end",
  },
  doubleItem: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    width: "100%",
  },
  itemBlue: {
    color: tokens.colorPaletteBlueForeground2,
  },
  fullWidth: {
    width: "100%",
    minWidth: "max-content",
  },
});

interface RouteDetailsCardProps {
  quote: RouteCostBrakeDown;
  quoteResultId: number;
  onHandleOfferPrice: (id: number, price: number, accepted: boolean) => void;
  onClose?: () => void;
  onPrint?: () => void;
}

const RouteDetailsCard: React.FC<RouteDetailsCardProps> = ({
  quote,
  quoteResultId,
  onHandleOfferPrice,
  onClose,
  onPrint,
}) => {
  const styles = useStyles();
  const { addNotification } = useNotification();
  const [offeredPrice, setOfferedPrice] = useState<number>(0);

  const withHazmat = quote.routeCost.loadType.includes("WithHazmat");
  const formattedLoadName = withHazmat
    ? quote.routeCost.loadType.replace("WithHazmat", "")
    : quote.routeCost.loadType;
  const formattedTotalPrice = formatToCurrency(quote.routeCost.totalCost ?? 0);
  const formattedPerMilePrice = formatToCurrency(
    quote.routeCost.costPerMile ?? 0,
  );
  const formattedPerMileWithEmptyPrice = formatToCurrency(
    quote.routeCost.costPerMileWithEmptyLoad ?? 0,
  );
  const formattedMarketAverageTotalCost = formatToCurrency(
    quote.marketAverageTotalCost ?? 0,
  );
  const formattedMarketAverageCostPerMile = formatToCurrency(
    quote.marketAverageCostPerMile ?? 0,
  );

  const companyDriverNetProfit = offeredPrice
    ? Number(offeredPrice) - (quote.companyDriverPay || 0)
    : 0;
  const companyDriverNetProfitMargin =
    companyDriverNetProfit !== 0
      ? (companyDriverNetProfit / +offeredPrice) * 100
      : 0;

  const offeredPriceOwnerOperatorNetProfit = offeredPrice
    ? Number(offeredPrice) - (quote.ownerOperatorPay || 0)
    : 0;
  const offeredPriceOwnerOperatorNetProfitMargin =
    offeredPriceOwnerOperatorNetProfit !== 0
      ? (offeredPriceOwnerOperatorNetProfit / +offeredPrice) * 100
      : 0;

  const onCopy = async () => {
    try {
      const template = `Pickup location: ${
        quote.originLocationAddress ? quote.originLocationAddress + ", " : ""
      }${quote.originLocationCity}, Zip ${
        quote.originLocationZipCode
      }\nDestination location: ${
        quote.destinationLocationAddress
          ? quote.destinationLocationAddress + ", "
          : ""
      }${
        quote.destinationLocationCity
      }, Zip ${quote.destinationLocationZipCode}\nTrip: ${
        formatMiles(quote.distanceInMiles).formattedValue
      } miles\nLoad type: ${formattedLoadName} ${
        withHazmat ? `+ hazmat` : ""
      }\nTotal: ${formattedTotalPrice}\nPrice per mile: ${formattedPerMilePrice}/mi\n`;

      await copyToClipboardAddin(template);

      addNotification({
        type: "success",
        message: "Route copied to clipboard",
      });
    } catch (error) {
      addNotification({
        type: "error",
        message: "Failed to copy route",
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.routeInfo}>
          <RouteConnector
            left={
              <div className={styles.locationWrapper}>
                <Text>MRK {quote.originMarket.cityName}</Text>
                <Text weight="bold">{quote.originLocationCity}</Text>
                {quote.originLocationAddress && (
                  <Text weight="bold">{quote.originLocationAddress}</Text>
                )}
                <Text>{quote.originLocationZipCode}</Text>
              </div>
            }
            right={
              <div className={styles.locationWrapper}>
                <Text>MRK {quote.destinationMarket.cityName}</Text>
                <Text weight="bold">{quote.destinationLocationCity}</Text>
                {quote.destinationLocationAddress && (
                  <Text weight="bold">{quote.destinationLocationAddress}</Text>
                )}
                <Text>{quote.destinationLocationZipCode}</Text>
              </div>
            }
          />

          <div className={styles.actions}>
            <RenderIf shouldRender={!!onPrint}>
              <Tooltip content="Print Details" relationship="label">
                <Button
                  icon={<Document24Regular />}
                  appearance="transparent"
                  size="small"
                  onClick={onPrint}
                />
              </Tooltip>
            </RenderIf>

            <RenderIf shouldRender={!!onClose}>
              <Tooltip content="Close" relationship="label">
                <Button
                  className={styles.closeButton}
                  appearance="transparent"
                  onClick={onClose}
                >
                  X
                </Button>
              </Tooltip>
            </RenderIf>
          </div>
        </div>

        <div className={styles.detailedInfo}>
          <div className={styles.group}>
            <Text weight="semibold">Company Driver</Text>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>Driver Pay</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.companyDriverPay ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>CD truck</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.truck ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>Fuel</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.fuel ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>Insurance</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.insurance ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>CD Expense</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.totalExpense ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>CD Net</Text>
              <div className={styles.doubleItem}>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.netProfit ?? 0)}
                </Text>
                <Text>({quote.netProfitMargin}%)</Text>
              </div>
            </div>
          </div>

          <div className={styles.group}>
            <Text weight="semibold">Owner Operator</Text>
            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>O/O Pay</Text>
              <Text className={styles.alignEnd}>
                {formatToCurrency(quote.ownerOperatorPay ?? 0)}
              </Text>
            </div>

            <div className={mergeClasses(styles.detailRow, styles.grayedOut)}>
              <Text className={styles.textWidth}>O/O Net</Text>
              <div className={styles.doubleItem}>
                <Text className={styles.alignEnd}>
                  {formatToCurrency(quote.ownerOperatorNetProfit ?? 0)}
                </Text>
                <Text>({quote.ownerOperatorNetProfitMargin}%)</Text>
              </div>
            </div>
          </div>
        </div>

        <Tooltip content="Copy Details" relationship="label">
          <div>
            <div className={styles.costInfo} onClick={onCopy}>
              <div className={styles.costInfoRow}>
                <Text className={styles.textWidth}>
                  {formattedLoadName} {withHazmat && "+ H"}
                </Text>

                <div className={styles.doubleItem}>
                  <Text className={styles.alignEnd}>{formattedTotalPrice}</Text>
                  <Text>{formattedPerMilePrice}/mi</Text>
                </div>
              </div>

              <RenderIf
                shouldRender={
                  !!quote.marketAverageTotalCost &&
                  !!quote.marketAverageCostPerMile
                }
              >
                <div className={`${styles.costInfoRow} ${styles.grayedOut}`}>
                  <Text className={styles.textWidth}>Market Avg.</Text>

                  <div className={styles.doubleItem}>
                    <Text className={styles.alignEnd}>
                      {formattedMarketAverageTotalCost}
                    </Text>
                    <Text>
                      {formattedMarketAverageCostPerMile}{" "}
                      {!!quote.marketBids && <span>({quote.marketBids})</span>}
                    </Text>
                  </div>
                </div>
              </RenderIf>

              <div className={`${styles.costInfoRow} ${styles.grayedOut}`}>
                <Text className={styles.textWidth}>Trip</Text>

                <div className={styles.doubleItem}>
                  <Text className={styles.alignEnd}>
                    {formatMiles(quote.routeDistanceInMiles).formattedValue}{" "}
                    miles
                  </Text>
                  {quote.emptyRouteDistanceInMiles && (
                    <Text>+{quote.emptyRouteDistanceInMiles} empty miles</Text>
                  )}
                </div>
              </div>

              <RenderIf
                shouldRender={!!quote.routeDistanceWithEmptyLoadInMiles}
              >
                <div className={`${styles.costInfoRow} ${styles.grayedOut}`}>
                  <Text className={styles.textWidth}>Trip /w empty</Text>

                  <div className={styles.doubleItem}>
                    <Text className={styles.alignEnd}>
                      {
                        formatMiles(
                          quote.routeDistanceWithEmptyLoadInMiles as number,
                        ).formattedValue
                      }{" "}
                      miles
                    </Text>
                    <Text>{formattedPerMileWithEmptyPrice}/mi /w empty</Text>
                  </div>
                </div>
              </RenderIf>
            </div>

            <Button
              style={{
                position: "absolute",
                bottom: "5px",
                right: "0px",
              }}
              icon={<Copy24Regular />}
              appearance="transparent"
              size="small"
              onClick={onCopy}
            />
          </div>
        </Tooltip>
      </div>

      <OfferPrice
        onAccepted={(offer) => onHandleOfferPrice(quoteResultId, offer, true)}
        onDeclined={(offer) => onHandleOfferPrice(quoteResultId, offer, false)}
        onOfferPriceChange={setOfferedPrice}
      />

      <div className={`${styles.costInfoRow} ${styles.itemBlue}`}>
        <Text className={styles.fullWidth}>Company Driver Net</Text>

        <div className={styles.doubleItem}>
          <Text className={styles.alignEnd}>
            {formatToCurrency(companyDriverNetProfit)}
          </Text>
          <Text>
            (
            {companyDriverNetProfitMargin > 0
              ? companyDriverNetProfitMargin.toFixed(0)
              : 0}
            %)
          </Text>
        </div>
      </div>

      <div className={`${styles.costInfoRow} ${styles.itemBlue}`}>
        <Text className={styles.fullWidth}>Owner Operator Net</Text>

        <div className={styles.doubleItem}>
          <Text className={styles.alignEnd}>
            {formatToCurrency(offeredPriceOwnerOperatorNetProfit)}
          </Text>
          <Text>
            (
            {offeredPriceOwnerOperatorNetProfitMargin > 0
              ? offeredPriceOwnerOperatorNetProfitMargin.toFixed(0)
              : 0}
            %)
          </Text>
        </div>
      </div>
    </div>
  );
};

export default RouteDetailsCard;
